import Box from "@mui/material/Box";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useNavigate } from "@tanstack/react-router";
import type { FC } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Typography } from "@/components/Typography";
import { useEnableCustomFieldCard } from "@/features/customField/useEnableCustomFieldCard";
import { trackGaEvent } from "@/features/ga/trackGaEvent";
import { useEnableGoogleAdsIntegrationMilestone1 } from "@/features/google-ads/useEnableGoogleAdsIntegration";
import { useEnableLineAdsIntegration } from "@/features/line-ads/useEnableLineAdsIntegration";
import { useEnableMcpConnections } from "@/features/mcp/useEnableMcpConnections";
import { useEnableMetaAdsIntegration } from "@/features/meta-ads/useEnableMetaAdsIntegration";
import { useEnableOpenApiV2KeySelfServe } from "@/features/openapi/useEnableOpenApiV2KeySelfServe";
import { useEnableProductFeedCard } from "@/features/productFeed/useEnableProductFeedCard";
import { interlude } from "@/interlude";
import {
  shouldShowGoogleAdsContactUs,
  shouldShowMetaAdsContactUs,
} from "@/pages/applications/adContactUs";
import { ApplicationCard } from "@/pages/applications/components/ApplicationCard";
import { CdhCard } from "@/pages/applications/components/CdhCard";
import { shouldShowMcpContactUs } from "@/pages/applications/mcpContactUs";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";
import CresclabImage from "@/statics/cresclab.png";
import GoogleAdsImage from "@/statics/googleAds.png";
import LineAdsBannerImage from "@/statics/lineAdsBanner.svg";
import MetaAdsBannerImage from "@/statics/metaAdsBanner.svg";

const styles = {
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  sectionDescription: {
    color: theme.colors.staticFgNote,
  },
  cardContainer: {
    display: "grid",
    gap: "20px",
    gridAutoRows: "auto",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(330px, 100%), 1fr))",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface ApplicationSectionProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

const ApplicationSection: React.FC<ApplicationSectionProps> =
  function ApplicationSection({ title, description, children }) {
    return (
      <Box component="section" sx={styles.section}>
        <Box>
          <Typography variant="h2" fontWeight={500}>
            {title}
          </Typography>
          <Box sx={styles.sectionDescription}>
            <Typography variant="body">{description}</Typography>
          </Box>
        </Box>
        <Box sx={styles.cardContainer}>{children}</Box>
      </Box>
    );
  };

interface CustomerDataSectionProps {
  customFieldButtonText: string;
  customFieldCount: number | undefined;
  customFieldLimit: number;
  enableCustomFieldCard: boolean;
  enableProductFeedCard: boolean;
  hasCustomFields: boolean;
  isCustomFieldLoading: boolean;
  isProductFeedConnected: boolean;
  isProductFeedLoading: boolean;
  onCustomFieldClick: () => void;
  onProductFeedClick: () => void;
  productFeedCount: number | undefined;
  productFeedModuleEnabled: boolean;
}

const CustomerDataSection: React.FC<CustomerDataSectionProps> =
  function CustomerDataSection({
    customFieldButtonText,
    customFieldCount,
    customFieldLimit,
    enableCustomFieldCard,
    enableProductFeedCard,
    hasCustomFields,
    isCustomFieldLoading,
    isProductFeedConnected,
    isProductFeedLoading,
    onCustomFieldClick,
    onProductFeedClick,
    productFeedCount,
    productFeedModuleEnabled,
  }) {
    const { t } = useTranslation();

    return (
      <ApplicationSection
        title={t("applications.customerData.title", "Customer data")}
        description={t(
          "applications.customerData.description",
          "Manage how customer data is unified, structured, and imported.",
        )}
      >
        <CdhCard />
        {enableProductFeedCard ? (
          <ApplicationCard
            title={t("applications.productFeed.title")}
            description={t("applications.productFeed.description")}
            detailText={
              productFeedCount === undefined
                ? undefined
                : t("applications.productFeed.configuredCount", {
                    count: productFeedCount,
                    defaultValue: "Configured feeds: {{count}}",
                  })
            }
            learnMoreLink={t("applications.productFeed.learnMore.link")}
            learnMoreText={t("common.learnMore")}
            buttonText={
              isProductFeedConnected
                ? t("common.manage", "Manage")
                : productFeedModuleEnabled
                  ? t("common.connect")
                  : t("common.contactUs")
            }
            onButtonClick={onProductFeedClick}
            imageUrl={CresclabImage}
            isConnected={isProductFeedConnected}
            isLoading={isProductFeedLoading}
            connectedLabel={t("common.connected")}
            connectedButtonText={t("common.manage", "Manage")}
          />
        ) : null}
        {enableCustomFieldCard ? (
          <ApplicationCard
            title={t("applications.customField.title")}
            description={t("applications.customField.description")}
            detailText={
              customFieldCount === undefined
                ? undefined
                : t("applications.customField.configuredCount", {
                    count: customFieldCount,
                    defaultValue: "Configured fields: {{count}}",
                  })
            }
            learnMoreLink={t("applications.customField.learnMoreUrl")}
            learnMoreText={t("common.learnMore")}
            buttonText={customFieldButtonText}
            onButtonClick={onCustomFieldClick}
            imageUrl={CresclabImage}
            isConnected={hasCustomFields && customFieldLimit > 0}
            isLoading={isCustomFieldLoading}
            connectedLabel={t("common.connected")}
            connectedButtonText={t("common.manage", "Manage")}
          />
        ) : null}
      </ApplicationSection>
    );
  };

interface AdvertisingSectionProps {
  enableGoogleAdsIntegration: boolean;
  enableLineAdsIntegration: boolean;
  enableMetaAdsIntegration: boolean;
  isGoogleAdsConnected: boolean;
  isGoogleAdsLoading: boolean;
  isLineAdsConnected: boolean;
  isLineAdsLoading: boolean;
  isMetaAdsConnected: boolean;
  isMetaAdsLoading: boolean;
  onGoogleAdsClick: () => void;
  onLineAdsClick: () => void;
  onMetaAdsClick: () => void;
  showGoogleAdsContactUs: boolean;
  showMetaAdsContactUs: boolean;
}

const AdvertisingSection: React.FC<AdvertisingSectionProps> =
  function AdvertisingSection({
    enableGoogleAdsIntegration,
    enableLineAdsIntegration,
    enableMetaAdsIntegration,
    isGoogleAdsConnected,
    isGoogleAdsLoading,
    isLineAdsConnected,
    isLineAdsLoading,
    isMetaAdsConnected,
    isMetaAdsLoading,
    onGoogleAdsClick,
    onLineAdsClick,
    onMetaAdsClick,
    showGoogleAdsContactUs,
    showMetaAdsContactUs,
  }) {
    const { t } = useTranslation();

    if (
      !enableGoogleAdsIntegration &&
      !enableMetaAdsIntegration &&
      !enableLineAdsIntegration
    )
      return null;

    return (
      <ApplicationSection
        title={t("applications.advertising.title", "Advertising")}
        description={t(
          "applications.advertising.description",
          "Connect advertising accounts to activate customer data and audiences.",
        )}
      >
        {enableGoogleAdsIntegration ? (
          <ApplicationCard
            title={t("applications.googleAds.title")}
            description={t("applications.googleAds.description")}
            learnMoreLink={t("applications.googleAds.learnMore.link")}
            learnMoreText={t("common.learnMore")}
            buttonText={
              showGoogleAdsContactUs
                ? t("common.contactUs")
                : t("common.connect")
            }
            onButtonClick={onGoogleAdsClick}
            imageUrl={GoogleAdsImage}
            isConnected={!showGoogleAdsContactUs && isGoogleAdsConnected}
            isLoading={isGoogleAdsLoading}
            connectedLabel={t("common.connected")}
            connectedButtonText={t("common.manage", "Manage")}
          />
        ) : null}
        {enableMetaAdsIntegration ? (
          <ApplicationCard
            title={t("applications.metaAds.title")}
            description={t("applications.metaAds.description")}
            buttonText={
              showMetaAdsContactUs ? t("common.contactUs") : t("common.connect")
            }
            onButtonClick={onMetaAdsClick}
            imageUrl={MetaAdsBannerImage}
            isConnected={!showMetaAdsContactUs && isMetaAdsConnected}
            isLoading={isMetaAdsLoading}
            connectedLabel={t("common.connected")}
            connectedButtonText={t("common.manage", "Manage")}
          />
        ) : null}
        {enableLineAdsIntegration ? (
          <ApplicationCard
            title={t("applications.lineAds.title", "LINE Ads")}
            description={t(
              "applications.lineAds.description",
              "Connect your LINE Ads account to sync ad audiences.",
            )}
            buttonText={t("common.connect")}
            onButtonClick={onLineAdsClick}
            imageUrl={LineAdsBannerImage}
            isConnected={isLineAdsConnected}
            isLoading={isLineAdsLoading}
            connectedLabel={t("common.connected")}
            connectedButtonText={t("common.manage", "Manage")}
          />
        ) : null}
      </ApplicationSection>
    );
  };

interface DeveloperToolsSectionProps {
  activeApiKeyCount: number | undefined;
  enableMcpConnections: boolean;
  enableOpenApi: boolean;
  hasApiKeys: boolean;
  hasMcpConnections: boolean;
  isMcpLoading: boolean;
  isOpenApiLoading: boolean;
  onMcpClick: () => void;
  onOpenApiClick: () => void;
  openApiV2Enabled: boolean;
  showMcpContactUs: boolean;
}

const DeveloperToolsSection: React.FC<DeveloperToolsSectionProps> =
  function DeveloperToolsSection({
    activeApiKeyCount,
    enableMcpConnections,
    enableOpenApi,
    hasApiKeys,
    hasMcpConnections,
    isMcpLoading,
    isOpenApiLoading,
    onMcpClick,
    onOpenApiClick,
    openApiV2Enabled,
    showMcpContactUs,
  }) {
    const { t } = useTranslation();

    if (!enableOpenApi && !enableMcpConnections) return null;

    return (
      <ApplicationSection
        title={t("applications.developerTools.title", "Developer tools")}
        description={t(
          "applications.developerTools.description",
          "Manage credentials used by your systems and integrations.",
        )}
      >
        {enableOpenApi ? (
          <ApplicationCard
            title={t("applications.openApi.title", "OpenAPI")}
            description={t(
              "applications.openApi.description",
              "Manage the API keys your systems use to call Crescendo Lab OpenAPI v2.",
            )}
            detailText={
              activeApiKeyCount === undefined
                ? undefined
                : t("applications.openApi.activeKeyCount", {
                    count: activeApiKeyCount,
                    defaultValue: "Active API keys: {{count}}",
                  })
            }
            learnMoreLink={t(
              "applications.openApi.learnMoreUrl",
              "https://doc.user360.cresclab.com/v2/",
            )}
            learnMoreText={t("common.learnMore")}
            buttonText={
              openApiV2Enabled
                ? t("applications.openApi.manage", "Manage")
                : t("common.contactUs")
            }
            onButtonClick={onOpenApiClick}
            imageUrl={CresclabImage}
            /**
             * A key count that failed to load leaves the badge off, which
             * looks the same as having no keys. Deliberately not given a
             * third state: the card's only job is to offer the way in, and
             * the page behind it reports a failed load on its own rather
             * than claiming the organization has none.
             */
            isConnected={openApiV2Enabled && hasApiKeys}
            isLoading={isOpenApiLoading}
            connectedLabel={t("common.connected")}
            /**
             * "Manage", not the component's default "Edit". Keys are revoked
             * and disabled more than they are edited, the page it opens is
             * called API Key Management, and this card already says "Manage"
             * before any key exists -- so the label does not change under the
             * reader when their first key is issued.
             */
            connectedButtonText={t("applications.openApi.manage", "Manage")}
          />
        ) : null}
        {enableMcpConnections ? (
          <ApplicationCard
            title={t("applications.mcp.title", "Crescendo Lab MCP")}
            description={t(
              "applications.mcp.description",
              "See and revoke the AI clients authorized to access your organization through Crescendo Lab MCP.",
            )}
            buttonText={
              showMcpContactUs
                ? t("common.contactUs")
                : t("applications.mcp.manage", "Manage")
            }
            onButtonClick={onMcpClick}
            imageUrl={CresclabImage}
            isConnected={hasMcpConnections}
            isLoading={isMcpLoading}
            connectedLabel={t("common.connected")}
            // "Manage" for the same reason as the OpenAPI card above.
            connectedButtonText={t("applications.mcp.manage", "Manage")}
          />
        ) : null}
      </ApplicationSection>
    );
  };

export const ApplicationsPage: FC = function ApplicationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const googleAdsManagerAccountQuery = interlude.googleAds.useGetManagerAccount(
    {
      queries: {
        checkAccess: false,
      },
    },
  );

  const isGoogleAdsConnected = googleAdsManagerAccountQuery.isSuccess;

  const organizationSettingQuery =
    interlude.organization.useOrganizationSetting();

  const productFeedListQuery = interlude.productFeed.useListFeeds();

  const enableGoogleAdsIntegration = useEnableGoogleAdsIntegrationMilestone1();

  const enableProductFeedCard = useEnableProductFeedCard();

  const enableMetaAdsIntegration = useEnableMetaAdsIntegration();

  const enableLineAdsIntegration = useEnableLineAdsIntegration();

  const enableCustomFieldCard = useEnableCustomFieldCard();

  const enableOpenApiV2KeySelfServe = useEnableOpenApiV2KeySelfServe();

  const enableMcpConnections = useEnableMcpConnections();

  const customFieldListQuery = interlude.customField.useListCustomFields({
    // Ask for single select fields and their options. Interlude leaves
    // them out unless a caller opts in.
    queries: { enable_single_select: true },
  });

  const metaAdsStatusQuery = interlude.metaAds.useGetStatus(undefined, {
    enabled: enableMetaAdsIntegration,
  });

  const isMetaAdsConnected =
    metaAdsStatusQuery.isSuccess &&
    metaAdsStatusQuery.data.tokenStatus === "active";

  const lineAdsGroupQuery = interlude.lineAds.useGetGroup(
    { queries: { checkAccess: false } },
    { enabled: enableLineAdsIntegration },
  );

  const hasEcModuleQuery = interlude.organization.useHasEcModule();
  const hasEcModule = hasEcModuleQuery.data?.hasEcModule ?? false;

  // Fail closed: absent level (legacy org) counts as not entitled.
  const isSegmentSyncAdPlatformOff =
    organizationSettingQuery.data?.maacFeatureSettings?.segmentSyncAdPlatform
      ?.level !== "on";

  const isAdIntegrationOff =
    organizationSettingQuery.data?.maacFeatureSettings?.adIntegration?.level !==
    "on";

  const showGoogleAdsContactUs = shouldShowGoogleAdsContactUs({
    segmentSyncOff: isSegmentSyncAdPlatformOff,
    adIntegrationOff: isAdIntegrationOff,
    hasEcModule,
  });

  const showMetaAdsContactUs = shouldShowMetaAdsContactUs({
    adIntegrationOff: isAdIntegrationOff,
  });

  const productFeedModuleEnabled =
    organizationSettingQuery.data?.productFeedModuleEnabled ?? false;

  const customFieldLimit = organizationSettingQuery.data?.customFieldLimit ?? 0;

  // Fail closed: an org whose setting has not loaded is not entitled yet.
  const openApiV2Enabled =
    organizationSettingQuery.data?.openApiV2Enabled ?? false;

  /**
   * Only asked for once the organization is entitled. The endpoint is gated on
   * the same setting and answers 403 `OPENAPI_V2_NOT_ENABLED` otherwise, and
   * this hub renders for every organization -- so an ungated call would sit
   * rejected behind every "Contact us" card in the product. The flag is in the
   * condition too, as it is for `metaAdsStatusQuery` above: nothing should be
   * fetched for a card this build does not render.
   */
  const apiKeyListQuery = interlude.apiKey.useListApiKeys(undefined, {
    enabled: enableOpenApiV2KeySelfServe && openApiV2Enabled,
  });

  // Fail closed, as `openApiV2Enabled` above.
  const mcpEnabled = organizationSettingQuery.data?.mcpEnabled ?? false;

  /**
   * Not gated on `mcpEnabled`, unlike the API key list: interlude answers this
   * endpoint for every organization, and the count is what decides whether an
   * organization without the entitlement still gets into the page.
   */
  const mcpApiKeyListQuery = interlude.mcp.useListMcpApiKeys(undefined, {
    enabled: enableMcpConnections,
  });

  const hasMcpConnections = (mcpApiKeyListQuery.data?.data.length ?? 0) > 0;

  const showMcpContactUs = shouldShowMcpContactUs({
    mcpEnabled,
    hasMcpConnections,
    isMcpConnectionListError: mcpApiKeyListQuery.isError,
  });

  const hasProductFeeds = useMemo(() => {
    return (productFeedListQuery.data?.feeds.length ?? 0) > 0;
  }, [productFeedListQuery.data?.feeds.length]);

  const isProductFeedConnected = useMemo(() => {
    return productFeedModuleEnabled && hasProductFeeds;
  }, [productFeedModuleEnabled, hasProductFeeds]);

  const hasCustomFields = useMemo(() => {
    return (customFieldListQuery.data?.length ?? 0) > 0;
  }, [customFieldListQuery.data?.length]);

  const activeApiKeyCount = useMemo(() => {
    if (!apiKeyListQuery.isSuccess) return 0;
    return apiKeyListQuery.data.data.filter(
      (apiKey) => apiKey.status.expected && apiKey.status.value === "active",
    ).length;
  }, [apiKeyListQuery.data, apiKeyListQuery.isSuccess]);

  const hasApiKeys = activeApiKeyCount > 0;

  /**
   * A disabled query stays `pending` forever, so the card cannot read
   * `isPending` on its own -- an organization without the entitlement would
   * spin instead of offering "Contact us". Only the entitled case waits for
   * the key count.
   */
  const isOpenApiCardLoading =
    organizationSettingQuery.isPending ||
    (enableOpenApiV2KeySelfServe &&
      openApiV2Enabled &&
      apiKeyListQuery.isPending);

  const customFieldButtonText = useMemo(() => {
    if (customFieldLimit <= 0) return t("common.contactUs");
    if (hasCustomFields) return t("common.manage", "Manage");
    return t("applications.customField.setup");
  }, [hasCustomFields, customFieldLimit, t]);

  const handleGoogleAdsClick = () => {
    if (showGoogleAdsContactUs) {
      trackGaEvent("gadsContactUsClick");
      window.open(t("newRequest.link"), "_blank", "noopener,noreferrer");
    } else {
      navigate({ to: "/applications/google-ads-integration" });
    }
  };

  const handleProductFeedClick = () => {
    if (productFeedModuleEnabled) {
      navigate({ to: "/applications/product-feeds" });
    } else {
      trackGaEvent("productFeedContactUsClick");
      window.open(t("newRequest.link"), "_blank", "noopener,noreferrer");
    }
  };

  const handleMetaAdsClick = () => {
    if (showMetaAdsContactUs) {
      window.open(t("newRequest.link"), "_blank", "noopener,noreferrer");
    } else {
      navigate({ to: "/applications/meta-ads-integration" });
    }
  };

  const handleLineAdsClick = () => {
    navigate({ to: "/applications/line-ads-integration" });
  };

  const handleOpenApiClick = () => {
    if (openApiV2Enabled) {
      navigate({ to: "/applications/api-keys" });
    } else {
      trackGaEvent("openApiContactUsClick");
      window.open(t("newRequest.link"), "_blank", "noopener,noreferrer");
    }
  };

  const handleMcpClick = () => {
    if (showMcpContactUs) {
      trackGaEvent("mcpContactUsClick");
      window.open(t("newRequest.link"), "_blank", "noopener,noreferrer");
    } else {
      navigate({ to: "/applications/mcp-connections" });
    }
  };

  const handleCustomFieldClick = () => {
    if (customFieldLimit > 0) {
      navigate({ to: "/applications/custom-fields" });
    } else {
      trackGaEvent("customFieldContactUsClick");
      window.open(t("newRequest.link"), "_blank", "noopener,noreferrer");
    }
  };

  return (
    <PageLayout>
      <PageTitle>
        {t("applications.dataIntegrations.title", "Data & integrations")}
      </PageTitle>
      <CustomerDataSection
        customFieldButtonText={customFieldButtonText}
        customFieldCount={
          customFieldLimit > 0 && customFieldListQuery.isSuccess
            ? customFieldListQuery.data.length
            : undefined
        }
        customFieldLimit={customFieldLimit}
        enableCustomFieldCard={enableCustomFieldCard}
        enableProductFeedCard={enableProductFeedCard}
        hasCustomFields={hasCustomFields}
        isCustomFieldLoading={
          customFieldListQuery.isPending || organizationSettingQuery.isPending
        }
        isProductFeedConnected={isProductFeedConnected}
        isProductFeedLoading={
          productFeedListQuery.isPending || organizationSettingQuery.isPending
        }
        onCustomFieldClick={handleCustomFieldClick}
        onProductFeedClick={handleProductFeedClick}
        productFeedCount={
          productFeedModuleEnabled && productFeedListQuery.isSuccess
            ? productFeedListQuery.data.feeds.length
            : undefined
        }
        productFeedModuleEnabled={productFeedModuleEnabled}
      />
      <AdvertisingSection
        enableGoogleAdsIntegration={enableGoogleAdsIntegration}
        enableLineAdsIntegration={enableLineAdsIntegration}
        enableMetaAdsIntegration={enableMetaAdsIntegration}
        isGoogleAdsConnected={isGoogleAdsConnected}
        isGoogleAdsLoading={
          googleAdsManagerAccountQuery.isPending ||
          hasEcModuleQuery.isPending ||
          organizationSettingQuery.isPending
        }
        isLineAdsConnected={lineAdsGroupQuery.isSuccess}
        isLineAdsLoading={lineAdsGroupQuery.isPending}
        isMetaAdsConnected={isMetaAdsConnected}
        isMetaAdsLoading={
          metaAdsStatusQuery.isPending || organizationSettingQuery.isPending
        }
        onGoogleAdsClick={handleGoogleAdsClick}
        onLineAdsClick={handleLineAdsClick}
        onMetaAdsClick={handleMetaAdsClick}
        showGoogleAdsContactUs={showGoogleAdsContactUs}
        showMetaAdsContactUs={showMetaAdsContactUs}
      />
      <DeveloperToolsSection
        activeApiKeyCount={
          openApiV2Enabled && apiKeyListQuery.isSuccess
            ? activeApiKeyCount
            : undefined
        }
        enableMcpConnections={enableMcpConnections}
        enableOpenApi={enableOpenApiV2KeySelfServe}
        hasApiKeys={hasApiKeys}
        hasMcpConnections={hasMcpConnections}
        isMcpLoading={
          organizationSettingQuery.isPending || mcpApiKeyListQuery.isPending
        }
        isOpenApiLoading={isOpenApiCardLoading}
        onMcpClick={handleMcpClick}
        onOpenApiClick={handleOpenApiClick}
        openApiV2Enabled={openApiV2Enabled}
        showMcpContactUs={showMcpContactUs}
      />
    </PageLayout>
  );
};
